import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { MarkSymbol } from "@/components/games/MarkSymbol";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTerms, setTerms } from "@/lib/termsStore";
import { BANKS as DEFAULTS, bankTitle } from "@/data/markBanks";

const input = "min-w-0 rounded-lg border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none";

const Row = ({ it, onChange, onDelete, testId }) => (
  <div className="flex flex-wrap items-center gap-2 border-t border-border py-2" data-testid={testId}>
    <div className="flex w-14 justify-center">{(it.g || it.sym) && <MarkSymbol item={it} size="sm" />}</div>
    {it.g ? <span className="w-40 text-sm text-muted-foreground">{it.name} (drawn sign)</span>
      : <input className={`${input} w-40`} value={it.sym} placeholder="Sign or word" onChange={(e) => onChange({ ...it, sym: e.target.value, ...(it.sym === it.name ? { name: e.target.value } : {}) })} data-testid={`${testId}-sym`} />}
    <input className={`${input} flex-1`} value={it.m} placeholder="Meaning" onChange={(e) => onChange({ ...it, m: e.target.value })} data-testid={`${testId}-meaning`} />
    <button onClick={onDelete} className="rounded-full p-2 text-muted-foreground hover:text-red-500" aria-label="Remove term" data-testid={`${testId}-delete`}><Trash2 size={15} /></button>
  </div>
);

const Bank = ({ k, items, setItems }) => (
  <section className="mt-6 rounded-2xl border border-border bg-card p-4" data-testid={`terms-bank-${k}`}>
    <h2 className="font-serif text-lg">{bankTitle(k)} <span className="text-sm text-muted-foreground">({items.length})</span></h2>
    {items.map((it, i) => (
      <Row key={it.id} it={it} testId={`term-${k}-${i}`} onChange={(n) => setItems(items.map((x, j) => (j === i ? n : x)))} onDelete={() => setItems(items.filter((_, j) => j !== i))} />
    ))}
    <button onClick={() => setItems([...items, { id: `custom-${Date.now()}`, sym: "", name: "", m: "", t: "term" }])} data-testid={`terms-add-${k}`}
      className="mt-2 inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm hover:border-primary"><Plus size={14} /> Add a term</button>
  </section>
);

// Admin: review and edit every sign, word and meaning used in the Signs & terms games.
export default function AdminTerms() {
  const { user } = useAuth();
  const live = useTerms();
  const [banks, setBanks] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setBanks(JSON.parse(JSON.stringify(live))); }, [live]);
  if (user && user.role !== "admin") return <div className="p-10">Admins only.</div>;
  const save = async () => {
    const clean = Object.fromEntries(Object.entries(banks).map(([k, items]) => [k, items.filter((x) => (x.g || x.sym.trim()) && x.m.trim()).map((x) => ({ ...x, name: x.name || x.sym }))]));
    setBusy(true);
    try { await api.put("/admin/game-terms", { banks: clean }); setTerms(clean); toast.success("Terms saved — the games use them now"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Put every term back to the original list?")) return;
    await api.delete("/admin/game-terms"); setTerms(null); setBanks(JSON.parse(JSON.stringify(DEFAULTS))); toast.success("Back to the original list");
  };
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-10">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="terms-back"><ArrowLeft size={14} /> Admin</Link>
        <h1 className="mt-4 font-serif text-4xl">Signs & terms for the games</h1>
        <p className="mt-2 text-sm text-muted-foreground">Every sign, word and meaning used in the Intermediate to Advanced lesson games. Edit, add or remove, then press Save. Each list needs at least 2 terms. Empty rows are dropped.</p>
        <div className="sticky top-20 z-20 mt-4 flex gap-2 rounded-2xl bg-background/90 py-2 backdrop-blur">
          <button onClick={save} disabled={busy || !banks} data-testid="terms-save" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
          <button onClick={reset} data-testid="terms-reset" className="rounded-full border border-border px-4 py-2 text-sm">Reset to original</button>
        </div>
        {banks && Object.keys(DEFAULTS).map((k) => <Bank key={k} k={k} items={banks[k] || []} setItems={(items) => setBanks({ ...banks, [k]: items })} />)}
      </main>
    </div>
  );
}
